import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import { QueturePlayer } from '@quebon/shared-learning'
import type { QuetureApiInterface } from '@quebon/shared-learning'
import { VideoDialog } from '@/components/common/VideoDialog'

interface SampleVideoSectionProps {
  titleHighlight: string
  title: string
  note: string
  posters: string[]
  videos: string[]
}

/**
 * #05 Sample video — white-on-top, blue gradient on bottom; player overlaps the
 * boundary; outside chevron nav arrows.
 * Figma: node 37:12557 (desktop) / 194:5862 (mobile)
 */
export function SampleVideoSection({
  titleHighlight,
  title,
  note,
  posters,
  videos,
}: SampleVideoSectionProps) {
  const { t: mt } = useTranslation('marketing')

  const [current, setCurrent] = useState(0)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const prev = () => setCurrent((c) => (c - 1 + posters.length) % posters.length)
  const next = () => setCurrent((c) => (c + 1) % posters.length)

  const quetureApi = useMemo<QuetureApiInterface>(
    () => ({
      getQuetureInfo: async () => {
        throw new Error('Direct hlsUrl used; getQuetureInfo should not be called')
      },
    }),
    [],
  )

  const openVideo = (index: number) => setOpenIndex(index)
  const closeVideo = () => setOpenIndex(null)

  return (
    <section id="sample-video" data-marketing-section="" className="relative w-full bg-white">
      {/* Header */}
      <div className="relative mx-auto max-w-[1136px] px-6 lg:px-0 pt-14 lg:pt-20">
        <div className="flex flex-col items-center gap-6 lg:gap-10 text-center tracking-[-0.6px]">
          <div className="flex flex-col items-center gap-2.5 lg:gap-2">
            <p className="font-['Pretendard',sans-serif] font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2] tracking-[-0.6px] text-qb-primary">
              {titleHighlight}
            </p>
            <p className="font-['Pretendard',sans-serif] font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2] tracking-[-0.6px] text-qb-text-high">
              {title}
            </p>
          </div>
          <p className="font-['Pretendard',sans-serif] font-normal text-[20px] lg:text-[24px] leading-[1.5] lg:leading-[1.4] tracking-[-0.6px] text-qb-text-mid">
            {note}
          </p>
        </div>
      </div>

      {/* Blue gradient backdrop (bottom half only) + player overlapping */}
      <div className="relative mt-10 lg:mt-12">
        {/* Bottom blue band — sits behind player, anchored to section bottom */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-[198px] lg:h-[400px] overflow-hidden"
        >
          <img
            src="/marketing/main/img-back.webp"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-70 blur-[8px] pointer-events-none select-none"
          />
          <div className="absolute inset-0 bg-[#3381ff]/80" />
        </div>

        {/* Player + nav row */}
        <div className="relative mx-auto max-w-[1136px] px-6 lg:px-0">
          <div className="flex justify-center items-center">
            {/* Left chevron */}
            <button
              onClick={prev}
              aria-label={mt('navigation.previousVideo')}
              className="hidden justify-center items-center w-20 h-20 text-white transition-opacity lg:flex shrink-0 hover:opacity-80"
            >
              <svg width="28" height="48" viewBox="0 0 28 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 4L4 24L24 44" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Video player */}
            <button
              type="button"
              onClick={() => openVideo(current)}
              aria-label={mt('media.playSample', { number: current + 1 })}
              className="relative w-full max-w-[360px] md:max-w-[640px] lg:max-w-[820px] mx-auto lg:mx-6 bg-white/10 backdrop-blur-[8px] rounded-[16px] lg:rounded-[32px] p-[5px] lg:p-3 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.2)] cursor-pointer group"
            >
              <div className="relative w-full aspect-video rounded-[8px] lg:rounded-[24px] overflow-hidden bg-black">
                <img
                  src={posters[current]}
                  alt={mt('media.sample', { number: current + 1 })}
                  className="object-cover w-full h-full"
                />
                {/* Play overlay */}
                <div className="flex absolute inset-0 justify-center items-center">
                  <img
                    src="/marketing/main/icon-play.webp"
                    alt=""
                    aria-hidden
                    className="w-10 h-10 md:w-20 md:h-20 lg:w-[120px] lg:h-[120px] transition-opacity group-hover:opacity-90"
                  />
                </div>
              </div>
            </button>

            {/* Right chevron */}
            <button
              onClick={next}
              aria-label={mt('navigation.nextVideo')}
              className="hidden justify-center items-center w-20 h-20 text-white transition-opacity lg:flex shrink-0 hover:opacity-80"
            >
              <svg width="28" height="48" viewBox="0 0 28 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4L24 24L4 44" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {/* Dots indicator */}
          <div className="flex items-center justify-center gap-1.5 mt-6 pb-10 lg:pb-20">
            {posters.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                aria-label={mt('media.video', { number: i + 1 })}
                className={[
                  'h-2 rounded-full transition-all',
                  i === current ? 'w-6 bg-white' : 'w-2 bg-white/50',
                ].join(' ')}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Video popup dialog */}
      <VideoDialog
        open={openIndex !== null}
        onOpenChange={(open) => {
          if (!open) closeVideo()
        }}
      >
        {openIndex !== null && videos[openIndex] && (
          <QueturePlayer
        renderMetadataMath
            key={videos[openIndex]}
            qid={`sample-${openIndex + 1}`}
            hlsUrl={videos[openIndex]}
            thumbnailUrl={posters[openIndex]}
            quetureApi={quetureApi}
            autoPlay
            allowSkip
          />
        )}
      </VideoDialog>
    </section>
  )
}
